import { CalendarData } from '@app/interfaces/lm/project';
import { TokenService } from '@services/sys/token.service';
import dayjs from 'dayjs';
import { SelectData, SelectOption } from '@app/interfaces/sys/dict';
import { EventEmitter, Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';

interface MessageConfig {
    key: string;
    value: string;
    color?: string;
}

interface Message {
    [key: string]: MessageConfig[];
}

@Injectable({
    providedIn: 'root',
})
export class SettingsService {
    layout: {
        y: number;
    } = {
        y: 0,
    };
    globalEvent = new EventEmitter();

    constructor(private tokenService: TokenService) {
    }

    setLayout(): void {
        const y = document.body.clientHeight - 261;
        this.layout = {
            ...this.layout,
            y,
        };
    }

    hasPermission(permissions: string[]): boolean {
        const userPermissions = this.tokenService.getUserPermissions();

        if (!permissions || permissions.length === 0) {
            return true;
        }

        return permissions.some((p: string) => userPermissions.includes(p));
    }

    initCalendar(startDate: string): Array<CalendarData[]> {
        const startDay = dayjs(startDate).startOf('M').startOf('w').toISOString();
        const endDay = dayjs(startDate).endOf('M').endOf('w').toISOString();
        const diffDay = dayjs(endDay).diff(dayjs(startDay), 'day');
        console.log('calendar day', diffDay);
        const data: CalendarData[][] = [];
        let item: CalendarData[] = [];
        const columnLength = 7;
        const rowLength = Math.ceil(diffDay / columnLength);
        let d = 0;
        new Array(rowLength).fill(null).forEach(() => {
            item = [];
            new Array(columnLength).fill(null).forEach(() => {
                item.push({
                    date: dayjs(startDay).add(d, 'day').toISOString(),
                });
                d++;
            });
            data.push(item);
        });
        return data;
    }

    submitPermission(
        requiredFields: string[],
        formData: {
            [key: string]: any;
        },
    ): boolean {
        if (!formData) {
            return false;
        }
        return !Object.keys(formData).some(
            (key) =>
                (requiredFields.includes(key) && (formData[key] === null || formData[key]?.length === 0)) || formData[key] === undefined,
        );
    }

    getDiffDay(date: string): string {
        if (!date) {
            return '';
        }
        const day = dayjs().diff(dayjs(date), 'day');
        return day > 0 ? day + ' days ago' : 'today';
    }

    getTimeValue(time: number, startTime?: number): number {
        const timeObj = dayjs(time);
        if (startTime) {
            // check the start time (current time) is over 23, then set the dueTime to 23:59:59, otherwise, 23:00:00
            return dayjs(startTime).get('hour') === 23
                ? timeObj.endOf('day').endOf('hour').endOf('minute').valueOf()
                : timeObj.endOf('day').startOf('hour').valueOf();
        }
        // Round up the time when the minute is passed over 30
        return timeObj.get('minute') >= 30 ? timeObj.add(1, 'h').startOf('h').valueOf() : timeObj.startOf('h').valueOf();
    }

    getPercent(count: number, totalCount: number): number {
        if (!count || !totalCount) {
            return 0;
        }
        return Number((count * 100 / totalCount).toFixed(2)) || 0;
    }

    getGaugeColor(value: number): string {
        let color = '';
        if (value < 75 && value >= 50) {
            color = '#ff8e03';
        } else if (value < 100 && value >= 75) {
            color = '#ffc107';
        } else if (value === 100) {
            color = '#5DA640';
        } else {
            color = '#C43349';
        }
        return color;
    }

    getTextWidth(text: string, offset: number = 30, fontPixel: number = 14): number {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        const font = `${fontPixel}px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif`
        if (context) {
            context.font = font;
            const metrics = context.measureText(text);
            // return Math.round(metrics.width) + offset > 400 ? 400 : Math.round(metrics.width) + offset;
            return Math.round(metrics.width) + offset;
        }
        return -1;
    }

    getCurrentDateValue(searchDate: Date): {
        startTime: number,
        endTime: number
    } {
        const date = dayjs(searchDate || new Date());
        return {
            startTime: date.startOf('d').valueOf(),
            endTime: date.endOf('d').valueOf(),
        };
    }

    getRangeString(x: {
        startTime: number,
        endTime: number
    }): string {
        return `${dayjs(x.startTime).format('YYYY-MM-DD HH:mm:ss')} to ${dayjs(x.endTime).format('YYYY-MM-DD HH:mm:ss')}`;
    }

    parseValue(str: string): any {
        let value = null;
        try {
            value = str ? JSON.parse(str) : null;
        } catch (e) {
            value = null;
        }
        return value;
    }

    formatSize(size: number): string {
        if (size > 1024 * 1024) {
            return (size / (1024 * 1024)).toFixed(1) + ' MB';
        } else if (size > 52) {
            return (size / 1024).toFixed(1) + ' KB';
        }
        return size + ' B';
    }

    formatSelectOptions(arr: SelectOption[]): SelectData {
        let data = {};
        arr.forEach(it => {
            data = {
                ...data,
                [it.value]: it
            };
        });
        return data;
    }

    sendGlobalEvent(name: string, value?: any): void {
        this.globalEvent.emit({ name, value });
    }

    watchGlobalEvent(name: string): Observable<any> {
        return this.globalEvent.pipe(
            filter((item) => item.name === name),
            map((item) => item.value)
        );
    }

}
